import { backgroundChecksApi } from "@api";
import { BackgroundCheck, RequirementType } from "@wingspanhq/wingspan-api-sdk";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";

import { queriesBackgroundChecks } from "../../../../v3/query/queriesBackgroundChecks";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { buildConfigFields } from "../../utils";

import { getBackgroundCheckFromCache } from "./configs/helpers/getBackgroundCheckFromCache";
import { usePayeeBackgroundChecksConfig } from "./configs/payee/usePayeeBackgroundChecksConfig";
import { usePayerBackgroundChecksConfig } from "./configs/payer/usePayerBackgroundChecksConfig";

/**
 * Hoisted so the batch read and `buildConfigFields` address the same cache
 * entry — the config resolves a missing document's pending/failed state through
 * this key.
 */
const getDataSourceQueryKey = (id: string) =>
  queriesBackgroundChecks.getBackgroundCheck.getQueryKey({
    backgroundCheckId: id
  });

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = getDataSourceQueries(
    getDataSourceQueryKey,
    requirements,
    id => backgroundChecksApi.getBackgroundCheck({ backgroundCheckId: id }),
    RequirementType.BACKGROUND_CHECK
  );

  return useWSQueries({ queries });
};

export const useBackgroundChecksRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.BACKGROUND_CHECK] => {
  const contextQueries = useContextQueries(requirements);
  const payerBackgroundChecksConfig = usePayerBackgroundChecksConfig(callback);
  const payeeBackgroundChecksConfig = usePayeeBackgroundChecksConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  // recompute only when the batch query data updates — the payer/payee configs
  // are new objects every render and must not invalidate the memo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fields = useMemo(
    buildConfigFields<BackgroundCheck, RequirementType.BACKGROUND_CHECK>({
      payerConfig: payerBackgroundChecksConfig,
      payeeConfig: payeeBackgroundChecksConfig,
      getAdditionalData: getBackgroundCheckFromCache,
      hasDataSource: true,
      getDataSourceQueryKey
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getBackgroundCheckFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    isFetching: contextQueries.some(query => query.isFetching),
    dataUpdatedAt: queriesHash
  };
};
